import { Capacitor } from '@capacitor/core';
/**
 * Push Notification Utilities
 * Handles service worker registration and push subscription management
 */

// VAPID public key (safe to expose - only private key is secret)
// Generate new keys with: npx web-push generate-vapid-keys
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || 'BHin079tmNgrfVEk_zzi_vkQUywzqnBXUs3-eFSQ7PumEuCt5q8rc_la6ifgdlQouK7jHurXq2sbdRLVY3HdhoA';

/**
 * Check if push notifications are supported
 */
export function isPushSupported() {
  // Web push can't run inside the iOS app's web view; the app gets native
  // push notifications of its own.
  if (Capacitor.isNativePlatform()) return false;
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', or 'default'
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('Notifications not supported');
  }
  
  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Register service worker
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers not supported');
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    
    console.log('✅ Service Worker registered:', registration.scope);
    
    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    
    return registration;
  } catch (error) {
    console.error('❌ Service Worker registration failed:', error);
    throw error;
  }
}

/**
 * Convert URL-safe base64 to Uint8Array (required for VAPID)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(registration) {
  if (!VAPID_PUBLIC_KEY) {
    throw new Error('VAPID public key not configured');
  }

  try {
    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      console.log('📱 Already subscribed to push notifications');
      return subscription;
    }

    // Subscribe to push
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });

    console.log('✅ Push subscription created:', subscription.endpoint);
    return subscription;
  } catch (error) {
    console.error('❌ Push subscription failed:', error);
    throw error;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      console.log('✅ Unsubscribed from push notifications');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('❌ Unsubscribe failed:', error);
    throw error;
  }
}

/**
 * Get current push subscription
 */
export async function getCurrentSubscription() {
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('❌ Failed to get subscription:', error);
    return null;
  }
}

/**
 * Save push subscription to backend
 */
export async function saveSubscriptionToServer(subscription, userEmail) {
  const { supabase } = await import('../lib/supabase');
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    throw new Error('Not authenticated');
  }

  const subscriptionData = subscription.toJSON();
  
  const response = await fetch('/api/push/subscribe', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify({
      endpoint: subscriptionData.endpoint,
      p256dh: subscriptionData.keys.p256dh,
      auth: subscriptionData.keys.auth,
      userEmail,
      userAgent: navigator.userAgent
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to save subscription');
  }

  return await response.json();
}

/**
 * Remove push subscription from backend
 */
export async function removeSubscriptionFromServer(endpoint) {
  const { supabase } = await import('../lib/supabase');
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch('/api/push/unsubscribe', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify({ endpoint })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove subscription');
  }

  return await response.json();
}

/**
 * Full setup flow: register SW, request permission, subscribe, save to server
 */
export async function setupPushNotifications(userEmail) {
  // Check support
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported on this device');
  }

  // Request permission
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }

  // Register service worker
  const registration = await registerServiceWorker();

  // Subscribe to push
  const subscription = await subscribeToPush(registration);

  // Save to server
  await saveSubscriptionToServer(subscription, userEmail);

  return subscription;
}

/**
 * Full teardown flow: unsubscribe and remove from server
 */
export async function teardownPushNotifications() {
  const subscription = await getCurrentSubscription();
  
  if (subscription) {
    // Remove from server first
    await removeSubscriptionFromServer(subscription.endpoint);
    
    // Then unsubscribe locally
    await unsubscribeFromPush();
  }
}

